import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getEmployeeComplaints } from '../../../redux/actions/SocietyPortal/ComplaintAction/ComplaintAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_COMPLAINTS,
    SOCIETY_PORTAL_COMPLAINTS_VIEW,
} from '../../../Routing/routes';

const STATUS_STYLE = {
    PENDING: 'tw-bg-blue-100 tw-text-blue-700',
    IN_PROGRESS: 'tw-bg-amber-100 tw-text-amber-700',
    COMPLETED: 'tw-bg-emerald-100 tw-text-emerald-700',
    REOPENED: 'tw-bg-rose-100 tw-text-rose-700',
};
const PRIORITY_STYLE = {
    LOW: 'tw-bg-slate-100 tw-text-slate-600',
    MEDIUM: 'tw-bg-blue-50 tw-text-blue-600',
    HIGH: 'tw-bg-amber-50 tw-text-amber-700',
    URGENT: 'tw-bg-rose-50 tw-text-rose-700',
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';

const EmployeeComplaints = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        employeeComplaintsLoading,
        employeeComplaintsData,
    } = useSelector(state => state.ComplaintReducer || {});

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (userId) dispatch(getEmployeeComplaints({ societyId, userId }));
    }, [dispatch, userId, societyId, refreshKey]);

    const rows = Array.isArray(employeeComplaintsData) ? employeeComplaintsData : [];

    const employee = rows[0]?.assignedTo;
    const employeeName = employee
        ? [employee.first_name, employee.last_name].filter(Boolean).join(' ').trim() || employee.email
        : `Employee #${userId}`;

    const counts = rows.reduce((acc, c) => {
        acc[c.status] = (acc[c.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-6xl tw-mx-auto tw-space-y-4">
                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS)}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Complaints
                </button>

                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Complaint History — {employeeName}</h1>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Every complaint assigned to this employee, with responses/remarks visible on each entry.</p>
                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
                        {Object.entries(STATUS_STYLE).map(([s, cls]) => (
                            <span key={s} className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${cls}`}>
                                {humanize(s)} · {counts[s] || 0}
                            </span>
                        ))}
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full tw-bg-gray-100 tw-text-gray-700">
                            Total · {rows.length}
                        </span>
                    </div>
                </div>

                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Code</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Subject</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Category</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Priority</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Status</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Due</th>
                                    <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Raised</th>
                                    <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employeeComplaintsLoading && (
                                    <tr>
                                        <td colSpan={8} className="tw-text-center tw-py-12">
                                            <div className="tw-animate-spin tw-inline-block tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500" />
                                        </td>
                                    </tr>
                                )}
                                {!employeeComplaintsLoading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                            No complaints assigned to this employee yet.
                                        </td>
                                    </tr>
                                )}
                                {!employeeComplaintsLoading && rows.map(c => (
                                    <tr key={c.complaint_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-slate-50/50">
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-font-mono tw-font-semibold">{c.complaint_code}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-800 tw-max-w-xs tw-truncate" title={c.subject}>{c.subject}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{c.category?.name || '—'}</td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${PRIORITY_STYLE[c.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                {humanize(c.priority)}
                                            </span>
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5">
                                            <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[c.status] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                {humanize(c.status)}
                                            </span>
                                        </td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{fmtDate(c.due_date)}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">{fmtDate(c.created_at)}</td>
                                        <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                            <button
                                                title="View responses / remarks"
                                                onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS_VIEW.replace(':id', c.complaint_id))}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-blue-600"
                                            >
                                                <Icon icon="mdi:eye-outline" className="tw-text-base" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeComplaints;
